import { formatDateRange } from '@/utils';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useGetEarlyComerRatioQuery } from '../../../../../../app/features/employee/management-dashboard/management-dashboard.api';
import useError from '../../../../../../hooks/useError';
import type { ManagementFilters } from '../../../../../../hooks/useManagementFilters';

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0F172A] text-white p-2 rounded border border-[#1f2d40] text-xs">
        <p className="text-center font-bold mb-1">
          {dayjs(label).format('MMM D, YYYY')}
        </p>
        <p className="text-[#FF00A2]">Ratio: {payload[0].value?.toFixed(2)}%</p>
      </div>
    );
  }

  return null;
};

const EarlyComerRatio = ({
  department_id,
  office_id,
  filter,
}: {
  department_id?: string;
  office_id?: string;
  filter: ManagementFilters;
}) => {
  const earlyComerFilter = filter.workForceCulture;

  const { data: ratioData, error } = useGetEarlyComerRatioQuery({
    start_date: earlyComerFilter.start_date || '',
    end_date: earlyComerFilter.end_date || '',
    department_id,
    office_id,
  });
  useError(error, 'Failed to fetch early comer trend stats');

  const chartData = (ratioData?.data?.data?.daily_breakdown || []).map(
    (item) => ({
      name: item.date,
      value: item.early_comer_ratio_percentage,
    }),
  );

  return (
    <div className="bg-theme-slate-900 border border-theme-gray-700 p-4 rounded-xl w-full">
      <Flex
        justify="space-between"
        align="center"
        className="mb-6 text-white p-2"
      >
        <h4 className="text-xl text-white">Early Comer Trend</h4>
        <span className="text-theme-primary">
          {formatDateRange(
            earlyComerFilter.start_date,
            earlyComerFilter.end_date,
            { separator: '-' },
          )}
        </span>
      </Flex>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorEarly" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FF00A2" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#FF00A2" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#1e2a38"
            vertical={false}
          />
          <XAxis
            dataKey="name"
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(val) => dayjs(val).format('MMM D')}
          />
          <YAxis
            stroke="#94a3b8"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: '#ffffff', strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#FF00A2"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorEarly)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EarlyComerRatio;
